import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
    forLifeOf,
    getDynamicRouterLink,
    getRouterLinkById,
    LifecycleAware,
    Icons
} from '@lsq-frontend/common-ui';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import {
    GetBuyerImplementationCompanyLocationResponse,
    GetBuyerImplementationCompanyLocationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-company-location';
import { ADMIN_MANAGE_BUYER_LOCATION, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ManageBuyerService } from '../manage-buyer.service';

@Component({
    selector: 'review-buyer-company-location',
    styleUrls: ['./review-buyer-section.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <buyer-review-section
            [sectionTitle]="'Buyer Location'"
            [editLocation]="editLocationUrl$ | async"
        >
            <div *ngIf="!cachedResponse">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div *ngIf="cachedResponse" class="review-buyer-section">
                <info-box *ngIf="isMissingData()">
                    You have missing information that needs your attention.
                </info-box>

                <div [style.padding]="'2rem 3rem'">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Office Name*
                                <lsq-icon
                                    id="officeNameWarning"
                                    *ngIf="!cachedResponse.officeName"
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="officeName">
                                {{ !cachedResponse.officeName ? '—' : cachedResponse.officeName }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Country Code*
                                <lsq-icon
                                    id="countryCodeWarning"
                                    *ngIf="!cachedResponse.countryCode"
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="countryCode">
                                {{ !cachedResponse.countryCode ? '—' : cachedResponse.countryCode }}
                            </p>
                        </div>

                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Address Line 1*
                                <lsq-icon
                                    id="addressLine1Warning"
                                    *ngIf="!cachedResponse.addressLine1"
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="addressLine1">
                                {{
                                    !cachedResponse.addressLine1 ? '—' : cachedResponse.addressLine1
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Address Line 2</p>
                            <p class="body text-dark-gray" id="addressLine2">
                                {{
                                    !cachedResponse.addressLine2 ? '—' : cachedResponse.addressLine2
                                }}
                            </p>
                        </div>

                        <div class="col-12 col-lg-6">
                            <p class="label">
                                City*
                                <lsq-icon
                                    id="cityWarning"
                                    *ngIf="!cachedResponse.city"
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="city">
                                {{ !cachedResponse.city ? '—' : cachedResponse.city }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                State*
                                <lsq-icon
                                    id="stateNameWarning"
                                    *ngIf="!cachedResponse.state"
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="state">
                                {{ !cachedResponse.state ? '—' : cachedResponse.state }}
                            </p>
                        </div>

                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Zip Code*
                                <lsq-icon
                                    id="zipCodeWarning"
                                    *ngIf="!cachedResponse.zipCode"
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="zipCode">
                                {{ !cachedResponse.zipCode ? '—' : cachedResponse.zipCode }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Phone Number</p>
                            <p class="body text-dark-gray" id="phoneNumber">
                                {{
                                    !cachedResponse.phoneNumber
                                        ? '—'
                                        : (cachedResponse.phoneNumber | phone)
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Location Email Address</p>
                            <p class="body text-dark-gray" id="emailAddress">
                                {{
                                    !cachedResponse.emailAddress ? '—' : cachedResponse.emailAddress
                                }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </buyer-review-section>
    `
})
@LifecycleAware()
export class ReviewBuyerCompanyLocationComponent implements OnInit {
    @Output() public completed = new EventEmitter<boolean>();
    private _fetchSubject = new Subject();

    public cachedResponse: GetBuyerImplementationCompanyLocationResponse;

    public editLocationUrl$: Observable<UrlTree>;
    public icons = Icons;

    constructor(
        private _getBuyerImplementationCompanyLocationService: GetBuyerImplementationCompanyLocationService,
        private _manageBuyerService: ManageBuyerService,
        private _router: Router
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.buyerKey$,
            this._fetchSubject.pipe(startWith(true))
        ])
            .pipe(
                forLifeOf(this),
                filter((buyerKey) => !!buyerKey)
            )
            .subscribe(async ([buyerKey, _]) => {
                if (buyerKey) {
                    this.cachedResponse = await this._getBuyerImplementationCompanyLocationService.executeOnce(
                        {
                            buyerImplementationKey: buyerKey
                        }
                    );
                }

                this.completed.emit(!this.isMissingData());
            });

        this.editLocationUrl$ = this._manageBuyerService.buyerKey$.pipe(
            map((buyerKey) => {
                const urlTree = this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, ADMIN_MANAGE_BUYER_LOCATION, {
                            noStandaloneLeadingSlash: true
                        }),
                        { buyerKey }
                    ),
                    {
                        queryParams: {
                            isImplementation: true
                        }
                    }
                );
                return urlTree;
            })
        );
    }

    public isMissingData(): boolean {
        return (
            !this.cachedResponse.officeName ||
            !this.cachedResponse.state ||
            !this.cachedResponse.countryCode ||
            !this.cachedResponse.zipCode
        );
    }
}
